import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import User16 from '@carbon/icons-react/es/user/16';
// eslint-disable-next-line no-restricted-imports
import { Avatar as AntAvatar, Badge as AntBadge, Image } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { UserStatus } from 'api/models';
import DeactivatedUser from 'assets/svgs/deactivated_user.svg';
import { Caption01 } from 'components/styles';
import { getThumbnailUrl, withCacheBuster } from 'utils/get-thumbnail-url';
import { initials } from 'utils/initials';
const ImageStyles = css`
  .ant-image {
    display: inherit;
    position: inherit;
    height: 100%;

    img {
      object-fit: cover;
      height: 100%;
    }
  }
`;
const CompanyAvatar = styled(AntAvatar)`
  border: solid 1px var(--white-100);
  bottom: -30%;
  height: 12px;
  right: 2px;
  top: auto;
  width: 12px;
  z-index: 1;

  ${ImageStyles};
`;
const BadgeIconWrapper = styled.div`
  background-color: var(--white-100);
  width: ${({ size })=>size === 'large' ? '14px' : size === 'small' ? '10px' : '12px'};
  height: ${({ size })=>size === 'large' ? '14px' : size === 'small' ? '10px' : '12px'};
  border: solid 0.5px var(--white-100);
  border-radius: 50%;
  top: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: inherit;
    height: inherit;
  }
`;
const Badge = styled(AntBadge)`
  ${CompanyAvatar}.ant-avatar {
    &-sm {
      height: 10px;
      width: 10px;
    }

    &-lg {
      height: 14px;
      width: 14px;
    }
  }
`;
const StyledAvatar = styled(AntAvatar)`
  align-items: center;
  display: inline-flex;
  justify-content: center;
  flex-shrink: 0;

  &.ant-avatar-image,
  &.ant-avatar-icon {
    background: var(--coolest-gray-20);
    flex-shrink: 0;
  }

  .ant-avatar-string {
    ${Caption01};
    ${({ $initialsFontSize })=>$initialsFontSize != null && css`
        font-size: ${$initialsFontSize}px;
      `};
    color: var(--white-100);
  }
  ${ImageStyles};
`;
const AvatarColors = [
    'red',
    'magenta',
    'purple',
    'blue',
    'cyan',
    'teal',
    'green',
    'orange',
    'yellow'
];
/**
 * @method getColor
 * @private
 * @param str
 * @description generates color schemes based on the string provided. It is used on #1
 * @returns
 */ const getColor = (str = '')=>{
    if (!str.length) {
        return 'var(--coolest-gray-20)';
    }
    const charcodeOfStr = initials(str).toLowerCase().charCodeAt(0);
    const colorIndex = Math.abs(charcodeOfStr - 'a'.charCodeAt(0));
    return `var(--${AvatarColors[colorIndex % 9]}-gradient)`;
};
/**
 * @description Used to get the avatar color as a 50% variant. Similar functionality to getColor function
 * @param str
 * @returns
 */ export const getColorVariantAsString = (str = '')=>{
    if (!str.length) {
        return 'var(--coolest-gray-20)';
    }
    const charcodeOfStr = initials(str).toLowerCase().charCodeAt(0);
    const colorIndex = charcodeOfStr - 'a'.charCodeAt(0);
    return `var(--${AvatarColors[colorIndex % 9]}-50)`;
};
/**
 * @description Custom wrapper for antd avatar component.
 * #1 Can be used to generate text based avatar if no image is provided
 * #2 Adds Alt to the Image provided from name
 */ const AvatarComponent = ({ className, name = '', src, companyLogoUrl, shape = 'circle', size = 'default', icon = /*#__PURE__*/ _jsx(User16, {}), isPlaceholder, userStatus, badgeIcon, initialsFontSize })=>{
    const hasEitherNameOrSrc = src != null || name.length > 0;
    const [hasErrored, setErrored] = useState(false);
    const [thumbnailFailed, setThumbnailFailed] = useState(false);
    const [hasCompanyErrored, setCompanyErrored] = useState(false);
    const validSrc = useMemo(()=>src != null && src !== '' && !hasErrored, [
        src,
        hasErrored
    ]);
    // a missing thumb_ variant must not hide a photo that exists at the original url
    const handleError = useCallback(()=>{
        if (!thumbnailFailed && getThumbnailUrl(src) !== src) {
            setThumbnailFailed(true);
            return;
        }
        setErrored(true);
    }, [
        src,
        thumbnailFailed
    ]);
    const imageSrc = withCacheBuster(thumbnailFailed ? src : getThumbnailUrl(src));
    const handleCompanyError = useCallback(()=>{
        setCompanyErrored(true);
    }, []);
    useEffect(()=>{
        setErrored(false);
        setThumbnailFailed(false);
    }, [
        src
    ]);
    useEffect(()=>{
        setCompanyErrored(false);
    }, [
        companyLogoUrl
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: companyLogoUrl != null && companyLogoUrl !== '' || badgeIcon != null ? /*#__PURE__*/ _jsx(Badge, {
            "data-cy": `avatar-badge-${name}`,
            count: badgeIcon ? /*#__PURE__*/ _jsx(BadgeIconWrapper, {
                size: size,
                children: badgeIcon
            }) : !hasCompanyErrored ? /*#__PURE__*/ _jsx(CompanyAvatar, {
                src: /*#__PURE__*/ _jsx(Image, {
                    src: withCacheBuster(companyLogoUrl),
                    alt: "company-logo",
                    preview: false,
                    onError: handleCompanyError
                }),
                // A numeric size makes antd emit inline width/height, which beats
                // CompanyAvatar's 12px and badges the avatar with a circle its own
                // size — overhanging the row's icon gap and touching the label.
                // Class sizes keep the Badge wrapper's 10/14px overrides.
                size: typeof size === 'number' ? undefined : size,
                shape: shape,
                "data-email-template-ignore": true
            }) : null,
            children: /*#__PURE__*/ _jsx(StyledAvatar, {
                $initialsFontSize: initialsFontSize,
                "data-rocketlane-avatar": true,
                className: className,
                icon: !hasEitherNameOrSrc && icon,
                shape: shape,
                size: size,
                src: userStatus === UserStatus.Inactive ? DeactivatedUser : validSrc ? /*#__PURE__*/ _jsx(Image, {
                    alt: name,
                    loading: "lazy",
                    src: imageSrc,
                    preview: false,
                    onError: handleError
                }) : null,
                style: userStatus === UserStatus.Inactive ? undefined : {
                    background: validSrc || isPlaceholder ? '' : getColor(name)
                },
                children: initials(name)
            })
        }) : /*#__PURE__*/ _jsx(StyledAvatar, {
            $initialsFontSize: initialsFontSize,
            "data-rocketlane-avatar": true,
            className: className,
            icon: !hasEitherNameOrSrc && icon,
            shape: shape,
            size: size,
            "data-cy": `avatar-badge-${name}`,
            src: userStatus === UserStatus.Inactive ? DeactivatedUser : validSrc ? /*#__PURE__*/ _jsx(Image, {
                loading: "lazy",
                alt: name,
                src: imageSrc,
                preview: false,
                onError: handleError
            }) : null,
            style: userStatus === UserStatus.Inactive ? undefined : {
                background: validSrc || isPlaceholder ? '' : getColor(name)
            },
            children: initials(name)
        })
    });
};
export const Avatar = /*#__PURE__*/ React.memo(AvatarComponent);
